/**
 * Enregistrement du service worker (modèle cra-template-pwa).
 *
 * Point d'entrée UNIQUE : `register()` est appelé une fois, par `index.js`, et
 * seulement en production (le build est le seul à contenir
 * `service-worker.js`). Les composants ne réenregistrent rien : ils lisent
 * l'état par `ecouterEtatServiceWorker()`.
 *
 * Historique : `index.js` désinscrivait le service worker et purgeait tous
 * les caches à chaque chargement, pendant que `PWAStatus` réenregistrait un
 * `/service-worker.js` absent du build. Le mode hors ligne affiché n'existait
 * donc pas.
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// --- État observable --------------------------------------------------------

const ETAT_INITIAL = {
  /** Un service worker contrôle (ou va contrôler) la page. */
  actif: false,
  /** L'enveloppe applicative est en cache : l'application s'ouvre hors ligne. */
  horsLigneDisponible: false,
  /** Une nouvelle version attend ; `appliquerMiseAJour()` l'active. */
  miseAJourDisponible: false,
};

let etat = { ...ETAT_INITIAL };
let enregistrement = null;
const abonnes = new Set();

function publier(changement) {
  etat = { ...etat, ...changement };
  abonnes.forEach((cb) => {
    try {
      cb(etat);
    } catch (e) {
      console.error(e);
    }
  });
}

/** État courant du service worker. */
export function etatServiceWorker() {
  return etat;
}

/**
 * S'abonner aux changements d'état ; le rappel reçoit aussitôt l'état courant.
 * @returns {() => void} désabonnement
 */
export function ecouterEtatServiceWorker(cb) {
  abonnes.add(cb);
  cb(etat);
  return () => abonnes.delete(cb);
}

/** Réservé aux tests. */
export function _reinitialiserPourTests() {
  etat = { ...ETAT_INITIAL };
  enregistrement = null;
  abonnes.clear();
}

// --- Enregistrement ---------------------------------------------------------

export function register(config) {
  if (process.env.NODE_ENV !== 'production' || !('serviceWorker' in navigator)) {
    return;
  }
  const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
  if (publicUrl.origin !== window.location.origin) {
    // PUBLIC_URL sur un autre domaine (CDN) : le service worker ne pourrait
    // pas contrôler la page.
    return;
  }

  const demarrer = () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
    } else {
      registerValidSW(swUrl, config);
    }
  };

  if (document.readyState === 'complete') demarrer();
  else window.addEventListener('load', demarrer, { once: true });
}

export function surveillerInstallation(registration, config) {
  enregistrement = registration;
  publier({
    actif: true,
    horsLigneDisponible: Boolean(registration.active),
    miseAJourDisponible: Boolean(registration.waiting && navigator.serviceWorker.controller),
  });

  registration.onupdatefound = () => {
    const installingWorker = registration.installing;
    if (installingWorker == null) return;
    installingWorker.onstatechange = () => {
      if (installingWorker.state !== 'installed') return;
      if (navigator.serviceWorker.controller) {
        // Une version précédente contrôle encore la page.
        publier({ miseAJourDisponible: true });
        config?.onUpdate?.(registration);
      } else {
        // Première installation : l'enveloppe est en cache.
        publier({ horsLigneDisponible: true });
        config?.onSuccess?.(registration);
      }
    };
  };
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => surveillerInstallation(registration, config))
    .catch((error) => {
      console.error("Erreur lors de l'enregistrement du service worker :", error);
    });
}

function checkValidServiceWorker(swUrl, config) {
  // En local, un autre projet a pu laisser un service worker sur ce port.
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log("Pas de connexion : l'application fonctionne en mode hors ligne.");
    });
}

/**
 * Active la version en attente puis recharge la page quand elle a pris la
 * main. Sans version en attente, demande simplement une vérification.
 */
export function appliquerMiseAJour() {
  const enAttente = enregistrement?.waiting;
  if (!enAttente) {
    enregistrement?.update?.();
    return;
  }
  let recharge = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (recharge) return;
    recharge = true;
    window.location.reload();
  });
  enAttente.postMessage({ type: 'SKIP_WAITING' });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}

/** Nombre d'entrées par cache (affichage dans PWAStatus). */
export async function getCacheInfo() {
  if (!('caches' in window)) return {};
  const cacheInfo = {};
  for (const cacheName of await caches.keys()) {
    const cache = await caches.open(cacheName);
    cacheInfo[cacheName] = (await cache.keys()).length;
  }
  return cacheInfo;
}

/** Vide tous les caches de l'origine (le précache se reconstitue au prochain chargement). */
export async function clearCache() {
  if (!('caches' in window)) return;
  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
}
